import { useEffect, useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { isAmount, parseAmount, toAmountField } from '@/pages/usage/money'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Budget = Schema<'BudgetOut'>
type Spend = Schema<'SpendOut'>

/**
 * Settings → Budget caps — S-D3's panel, control for control.
 *
 * **The alert at the top is the panel's whole argument** and is quoted from the
 * approved screen: *"The cap is checked before every paid call. Not after. When
 * the cap is reached, runs stop cleanly — nothing is left half-done."* That is
 * a claim about `app/metering/service.py`'s pre-flight, and it is true because
 * the reservation is taken before the vendor call rather than the charge being
 * recorded after it.
 *
 * **An empty cap field means no cap, and the help text says so.** A tenant who
 * has never opened this panel has no ceiling at all — the same reading an
 * absent `seats` entitlement gets — so clearing the field has to be
 * expressible, and it is: the request sends `null` rather than omitting the
 * field.
 *
 * **Owner and admin, from S-D3's role matrix.** *Change budget caps* has a tick
 * in both columns, unlike *Billing & cancellation* one row below it — so a
 * member sees the panel read-only rather than not at all, because the number
 * governs work they are doing.
 */
export function BudgetPanel(): JSX.Element {
  const { role } = useActiveTenant()
  const toast = useToast()
  const editable = may(role, 'budget:manage')

  const budget = useQuery((signal) => call(() => api.GET('/api/v1/usage/budget', { signal })), [])

  const [cap, setCap] = useState('')
  const [warn, setWarn] = useState<string>('80')
  const [pause, setPause] = useState(true)

  // Re-seeded whenever the server answers, so a save in another tab does not
  // leave this form showing what used to be true.
  useEffect(() => {
    if (budget.state.status !== 'ready') return
    const saved = budget.state.data.budget
    setCap(toAmountField(saved.cap_minor))
    setWarn(saved.warn_at_percent === null ? 'never' : String(saved.warn_at_percent ?? 80))
    setPause(saved.pause_on_cap)
  }, [budget.state])

  const save = useMutation(
    (body: { cap_minor: number | null; warn_at_percent: number | null; pause_on_cap: boolean }) =>
      call(() => api.PUT('/api/v1/usage/budget', { body })),
  )

  if (budget.state.status === 'loading') return <Loading label="Loading budget caps" />
  if (budget.state.status === 'error') {
    return <ErrorState failure={budget.state.failure} onRetry={budget.refetch} />
  }

  const saved: Budget = budget.state.data.budget
  const spend: Spend = budget.state.data.spend
  const capValid = cap.trim() === '' || isAmount(cap)

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const result = await save.run({
      cap_minor: cap.trim() === '' ? null : parseAmount(cap),
      warn_at_percent: warn === 'never' ? null : Number(warn),
      pause_on_cap: pause,
    })
    if (!result.ok) return
    budget.refetch()
    toast.show({ kind: 'success', title: 'Budget caps saved' })
  }

  return (
    <form
      className="ar-card"
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      <div className="ar-card__header">
        <h3>Budget caps</h3>
        {editable ? null : <span className="ar-badge ar-badge--neutral">Read only</span>}
      </div>

      <div className="ar-card__body ar-stack">
        <div className="ar-alert ar-alert--info">
          <span className="ar-alert__icon" aria-hidden="true">
            ◷
          </span>
          <div className="ar-alert__body">
            <strong>The cap is checked before every paid call</strong>
            Not after. When the cap is reached, runs stop cleanly — nothing is left half-done.
          </div>
        </div>

        <InlineError failure={save.state.failure} />

        <Field
          label={`Monthly cap (${saved.currency})`}
          error={capValid ? undefined : 'Enter an amount, or leave it empty for no cap.'}
          help={
            saved.cap_minor === null
              ? 'No cap is set, so nothing is stopped. Leave it empty to keep it that way.'
              : `You've used ${spend.spend_display}${
                  spend.percent === null ? '' : ` (${String(spend.percent)}%)`
                } this month.`
          }
        >
          {(aria) => (
            <div className="ar-input-group">
              <input
                className="ar-input ar-num"
                inputMode="decimal"
                {...aria}
                disabled={!editable}
                value={cap}
                onChange={(event) => {
                  setCap(event.target.value)
                  save.reset()
                }}
              />
              <span className="ar-input-group__addon">{saved.currency}</span>
            </div>
          )}
        </Field>

        <Field label="Warn me at" help="One notification per month, when spend first passes it.">
          {(aria) => (
            <select
              className="ar-select"
              {...aria}
              disabled={!editable}
              value={warn}
              onChange={(event) => {
                setWarn(event.target.value)
                save.reset()
              }}
            >
              <option value="80">80% of cap</option>
              <option value="90">90% of cap</option>
              <option value="never">Never</option>
            </select>
          )}
        </Field>

        <label className="ar-switch">
          <input
            type="checkbox"
            disabled={!editable}
            checked={pause}
            onChange={(event) => {
              setPause(event.target.checked)
              save.reset()
            }}
          />
          <span className="ar-switch__track" aria-hidden="true" />
          <span>
            Pause everything when the cap is reached
            <span className="ar-choice__desc">Off means we keep working and bill the overage.</span>
          </span>
        </label>

        {saved.overage_cap_minor === null ? null : (
          <p className="ar-subtle">
            A one-off raise to {saved.overage_display} applies for this month only. The cap above
            comes back next month.
          </p>
        )}
      </div>

      <div className="ar-card__footer">
        <span className="ar-card__spacer" />
        <Button
          type="submit"
          tone="primary"
          disabled={!editable || !capValid}
          busy={save.state.status === 'running'}
        >
          Save
        </Button>
      </div>
    </form>
  )
}
